import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { RequestService } from '../../../services/request.service';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import {
  catchError,
  debounceTime,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { merge, of as observableOf } from 'rxjs';
import { FilterEnabledComponent } from '../../../helpers/filter-enabled.component';
import Contact from '../../../model/Contact/contact';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import { ModalService } from '../../../services/modal.service';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ContactHelper } from '../../../helpers/contact-helper';

@Component({
  selector: 'contacts-overview',
  templateUrl: './contacts-overview.component.html',
  styleUrls: ['./contacts-overview.component.scss'],
  providers: [FormatPhoneNumber],
})
export class ContactsOverviewComponent
  extends FilterEnabledComponent
  implements OnInit
{
  contacts: Contact[];
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  reloadData: EventEmitter<any>;
  data: Contact[] = [];
  displayedColumns: string[] = [
    'id',
    'firstName',
    'lastName',
    'phone',
    'mobile',
    'email',
    'star',
  ];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private requestService: RequestService,
    private router: Router,
    private datepipe: DatePipe,
    private formatPhonenumber: FormatPhoneNumber,
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private modalService: ModalService
  ) {
    super(
      [
        { key: 'id', active: false, strictEquality: true, numeric: true },
        {
          key: 'firstName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'lastName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'joinedPhoneNumber__contacts',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'mobileNumber__contacts',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'emails__email',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService
    );
    this.contacts = [];
  }

  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    this.reloadData = new EventEmitter<any>();

    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;

    this.sort.sortChange.subscribe(() => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
    });

    this.filtersChanged.pipe(debounceTime(300)).subscribe(() => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
      this.reloadData.emit();
    });

    merge(this.sort.sortChange, this.paginator.page, this.reloadData)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.pageIndexService.setPageIndex(
            this.getLocalStorageKey(),
            this.paginator.pageIndex
          );
          this.pageIndexService.setPageSize(
            this.getLocalStorageKey(),
            this.paginator.pageSize
          );
          return this.requestService.get(
            '/contacts',
            null,
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex,
            this.generateFilterString()
          );
        }),
        map((data) => {
          this.totalItems = Number.parseInt(data.headers.get('X-Total-Count'));
          return data.body;
        }),
        catchError(() => {
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.preprocessContacts(data);
        this.contacts = data;
        if (
          (data == null || data.length === 0) &&
          this.paginator.pageIndex > 0
        ) {
          this.paginator.pageIndex = 0;
          this.reloadData.next();
        }
      });
  }

  createContact() {
    this.router.navigateByUrl('/contacten/create');
  }

  editRow(row: Contact): void {
    this.router.navigate(['/contacten/edit', row.id]);
  }

  deleteContact(contact) {
    this.modalService.confirmDialog(
      'Contact verwijderen',
      'Bent u zeker dat u dit contact wilt verwijderen?',
      (answer) => {
        if (answer) {
          this.requestService
            .delete('/contacts/archive/' + contact.id)
            .subscribe(() =>
              this.requestService
                .get(
                  '/contacts',
                  null,
                  this.sort.active,
                  this.sort.direction,
                  30,
                  this.paginator.pageIndex,
                  this.generateFilterString()
                )
                .subscribe((data) => (this.contacts = data.body))
            );
        }
      }
    );
  }

  formatDate(date: Date) {
    return this.datepipe.transform(date, 'dd/MM/yyyy');
  }

  private preprocessContacts(contacts: Contact[]) {
    if (!CollectionHelper.isPopulated(contacts)) {
      return;
    }
    contacts.forEach((c) => {
      ContactHelper.setPhoneNumberDisplays(c, this.formatPhonenumber);
      if (CollectionHelper.isPopulated(c.emails)) {
        c.emailDisplay = c.emails[0].email;
      }
    });
  }

  protected getLocalStorageKey(): string {
    return 'ContactsOverviewComponent';
  }
}
